import { Animated, Pressable, StyleSheet, Text, View } from 'react-native';

import type { useTheme } from './theme';

// The pulsing INSTRUCTION LAYER drawn over the effort meter — the frame around
// the scale and the pill on the band, the things that appear, get read, get out
// of the way and come back. Third sibling of <EffortAxisDuration> (the axis
// row) and <EffortTimerSlot> (the column): FocusPanelEffort stays the
// coordinator and decides WHEN to invite and WHAT the words are, while each
// row-sized piece of CONTENT lives in a file of its own.
//
// This file is pure render. It owns no hook, no state and no t(): the caller
// hands it an Animated.Value (always useEffortNudge's — one cue engine, never a
// second animation) and an already-translated string, mirroring
// EffortDurationAxisLeading's `word` prop for the same reason. The cue file
// decides how a cue LOOKS, never what it says or when it shows.
//
// Both cues are attention cues, never the only signal of anything, and both are
// a11y-hidden: the meter's own segment hints already say what a tap does, and
// the frame is decoration around a scale that reads on its own.
//
// (CuePill in packages/ui carries the note that FocusPanelEffort's pill stays
// separate from the shared brand pill: its fill is a translucent LAYER so the
// effort scale shows THROUGH it, a distinct requirement. That is still true
// here — this extraction moves the pill, it does not re-litigate CN-brand-pill.)
//
// <EffortNudgeOptOut> (AP-0r) is the one exception to "no interactive
// content": the inline off-switch that rides alongside the reminder CTA.
// Unlike the pill and the frame it is a REAL control — a screen reader must
// be able to reach it for the whole time the set is pending, not only during
// the ~2.4s a sighted cue happens to be mid-blink — so it renders in normal
// flow, BELOW the band, never on the absolute layer stacked over the band's
// own tap targets. Long-press only (the codebase's established gesture for a
// permanent, hard-to-undo flip — see useWorkoutDelete's onLongPressDelete):
// a control that turns a preference off for good must be hard to trigger
// with a stray tap while reaching for the band underneath it.

/** The brand outline hugging the effort scale (AP-0l, Antenore: "rimetti il box
 *  che lampeggiava attorno alla scala"). A BORDER, never a fill, so it can
 *  never obscure the scale underneath ("transparent enough to see the scale"). */
export function EffortNudgeFrame({
  opacity,
  theme,
}: {
  /** Pulse driver — useEffortNudge's value, 0 at rest. */
  opacity: Animated.Value;
  theme: ReturnType<typeof useTheme>;
}) {
  const styles = makeStyles(theme);
  return (
    <Animated.View
      testID="effort-nudge-frame"
      pointerEvents="none"
      style={[styles.nudgeFrame, { opacity }]}
    />
  );
}

/** The translucent instructional pill centred on the BAND — "tap to log",
 *  "tap to stop and log", and whatever the next variant needs to teach. */
export function EffortBandCue({
  opacity,
  inset,
  label,
  singleLine,
  theme,
}: {
  /** Pulse driver — useEffortNudge's value, 0 at rest. */
  opacity: Animated.Value;
  /** Right inset in points: the caption sits on the band, so it has to stop
   *  where the band stops — the meter's fixed action column is not its to
   *  cover. 0 when the band owns the whole row. Owned by the caller, which is
   *  the only place that knows whether a column is there. */
  inset: number;
  /** Already-translated, SHORT: a verb, not an explanation. */
  label: string;
  /** Clamp to one line. On by default nowhere: the long "tap to log" invitation
   *  is allowed to wrap on a narrow screen, while the captions that share the
   *  row with a running timer's controls must not grow the overlay. */
  singleLine?: boolean;
  theme: ReturnType<typeof useTheme>;
}) {
  const styles = makeStyles(theme);
  return (
    <Animated.View
      pointerEvents="none"
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={[styles.ctaOverlay, { right: inset, opacity }]}
    >
      <View style={styles.ctaPill}>
        <View style={styles.ctaPillBg} pointerEvents="none" />
        <Text style={styles.ctaText} numberOfLines={singleLine ? 1 : undefined}>
          {label}
        </Text>
      </View>
    </Animated.View>
  );
}

/** Inline "turn it off" affordance for the reminder pulse (AP-0r), wired to
 *  the SAME `effortNudgePreference` Settings already uses — the caller owns
 *  the confirm and the preference write, this file only reports the tap.
 *  Sits BELOW the band in normal flow, quiet enough not to compete with the
 *  effort question above it.
 *
 *  A PLAIN TAP, not a long-press. The first version used long-press, copying
 *  the convention used for deleting a program — but that convention is for
 *  DESTRUCTIVE, hard-to-undo actions, and this is neither: it silences a
 *  reminder and Settings turns it back on. Long-press cost three things that
 *  matter more than the extra friction: a sighted athlete gets no cue that
 *  holding does something a tap doesn't (the label reads like a button and
 *  behaves like a dead one), VoiceOver's double-tap does not reach
 *  `onLongPress` on iOS at all, and the guard was redundant anyway — the
 *  caller's confirm is what makes a stray touch write nothing. */
export function EffortNudgeOptOut({
  label,
  hint,
  onPress,
  theme,
}: {
  /** Short verb, already translated — doubles as the visible text and the
   *  accessibilityLabel, same contract as EffortBandCue's `label`. */
  label: string;
  /** Says what happens and the way back, since the confirm follows. */
  hint: string;
  /** The confirm flow lives in the caller; this file only reports the tap. */
  onPress: () => void;
  theme: ReturnType<typeof useTheme>;
}) {
  const styles = makeStyles(theme);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={hint}
      onPress={onPress}
      style={({ pressed }) => [styles.optOut, pressed && styles.optOutPressed]}
    >
      <Text style={styles.optOutText} numberOfLines={1}>
        {label}
      </Text>
    </Pressable>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // Anchored to the BOTTOM, not stretched over the whole block: what it
    // invites you to touch is the BAND, which is the last row of the meter.
    // Stretched full-height it centred itself between the axis and the band,
    // and while a timer runs the axis is a row of buttons — so the caption sat
    // on top of the pause and the cancel (seen on device, 2026-07-22). 44 is
    // the band's own height, so the pill lands on it in every state.
    ctaOverlay: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 44,
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Brand instructional pill — mirrors the FocusPanel advance-cue look, but
    // its fill is a SEPARATE translucent layer (ctaPillBg) so the effort scale
    // stays visible through it (Antenore AP-0l: "abbastanza trasparente per
    // vedere la scala sotto"). The border + text stay solid.
    ctaPill: {
      borderColor: p.brand500,
      borderWidth: 1,
      borderRadius: r.r2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s1,
      overflow: 'hidden',
    },
    // Translucent fill only — opacity on the bg LAYER, not on the text sibling,
    // so the digits show through ~20% while the CTA text stays readable.
    ctaPillBg: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: p.brand100,
      opacity: 0.8,
    },
    // md, not sm: a standalone "tap here" CTA for a myopic user reads bigger
    // than the inline advance-cue it mirrors (a11y audit AP-0i). The DS-11 debt
    // this comment used to track is PAID: `brandText` is the text half of the
    // old dual-role brand600, gated >=4.5:1 on brand100 in every family, so
    // this CTA no longer leans on the default family to clear the floor.
    ctaText: {
      color: p.brandText,
      fontSize: ty.sizes.md,
      fontWeight: '800',
    },
    // Reminder frame (AP-0l, restored) — a brand outline hugging the effort
    // scale, opacity 0 at rest, pulsed by useEffortNudge. A border, so it never
    // hides the scale underneath. An attention cue, not a state.
    nudgeFrame: {
      position: 'absolute',
      top: -4,
      left: -4,
      right: -4,
      bottom: -4,
      borderWidth: 2,
      borderColor: p.brand500,
      borderRadius: r.r3,
    },
    // Right-aligned under the band, on its OWN row (never over the segments):
    // quiet enough that it never competes with the effort question above it.
    // minHeight, not hitSlop + padding arithmetic — same fix as questionWrap
    // above, for the same reason: independent of the (locale-varying) text
    // height, this still clears the ≥44pt floor (a11y non-negotiable).
    optOut: {
      alignSelf: 'flex-end',
      minHeight: 44,
      justifyContent: 'center',
      marginTop: s.s1,
    },
    optOutPressed: { opacity: 0.6 },
    // ink2/sm, NOT ink3/xs. Quiet is the intent, but ink3 at 11px measures
    // ~4.2:1 on the light surface and ~4.4:1 on the dark one, under the 4.5:1
    // floor — and this is the only visible way to reach the preference from
    // here, so it has to be readable to work at all.
    optOutText: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: '600',
    },
  });
}
